/**
 * BookingRequests - somebody has asked to book this student
 * ----------------------------------------------------------
 * The list of requests waiting on a student, with Accept and No thanks.
 *
 * WHY IT IS A COMPONENT RATHER THAN PART OF A PAGE. It is shown in two
 * places: "My offer", where a student manages their availability, and "My
 * work", which is where the notification about a request actually sends them.
 * Until 24 September 2026 it was only on the first of those, so somebody who
 * tapped "Someone would like to book you" landed on a page that said nothing
 * about it - the owner reported exactly that.
 *
 * Two lists of the same thing drift, and this is not a list that may drift:
 * the rule that holds the offers feature together is that the student decides
 * knowing what they are deciding about (HANDOFF section 10, rule 3). EVERY
 * date is shown before the Accept button, with the per-visit rate and the
 * total spelled out separately, because a student agreeing to six evenings
 * for "EUR 15" without being told it is EUR 90 has not been given the number
 * they would decide on. That paragraph has to be true in both places, so
 * there is one of it.
 */

import { Check } from 'lucide-react';

import Badge from './Badge';
import { dateTime, money } from '../utils/format';
import { useI18n } from '../hooks/useI18n';

export default function BookingRequests({ requests, onRespond, busy = false }) {
  const { t } = useI18n();

  const pending = (requests || []).filter((r) => r.status === 'pending');
  if (pending.length === 0) return null;

  return (
    <div className="card" style={{ marginBottom: 'var(--space-lg)', borderColor: 'var(--color-amber)' }}>
      <h3 style={{ marginBottom: 'var(--space-xs)' }}>
        {t('myOffer.askedToBookYou', { count: pending.length })}
      </h3>
      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
        {t('myOffer.nothingBooked')}
      </p>

      <div className="stack" style={{ gap: 'var(--space-sm)' }}>
        {pending.map((r) => (
          <div key={r._id} className="card">
            <div className="row-between" style={{ flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
              <div>
                <strong>{r.title}</strong>
                <div className="text-sm text-muted">
                  {r.poster?.firstName} {r.poster?.lastName?.[0]}. &middot; {r.city}
                  {(r.slots?.length || 1) === 1 && (
                    <> &middot; {r.slot ? dateTime(r.slot.start) : ''}</>
                  )}
                </div>
              </div>
              {/* Pay is per visit. On a multi-date request the badge shows
                  what the whole thing comes to, because that is the number
                  somebody decides on - with the rate spelled out below so
                  neither figure can be mistaken for the other. */}
              <Badge tone="green">{money(r.pay * (r.slots?.length || 1))}</Badge>
            </div>

            {/* EVERY DATE, before the Accept button. See the note at the top. */}
            {(r.slots?.length || 0) > 1 && (
              <div style={{ marginTop: 'var(--space-xs)' }}>
                <p className="text-sm">
                  <strong>{t('myOffer.separateTimes', { count: r.slots.length })}</strong> &middot;{' '}
                  {t('myOffer.eachAmount', { amount: money(r.pay) })} &middot;{' '}
                  {t('myOffer.inAll', { amount: money(r.pay * r.slots.length) })}
                </p>
                <ul className="visit-list" style={{ marginTop: 6 }}>
                  {r.slots.map((s) => (
                    <li className="visit-row" key={s.start}>
                      <span className="visit-when">{dateTime(s.start)}</span>
                      <span className="visit-note">
                        {t('myOffer.until', {
                          time: new Date(s.end).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          }),
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted" style={{ marginTop: 6 }}>
                  {t('myOffer.yesAgreesAll')}
                </p>
              </div>
            )}

            {r.message && <p style={{ marginTop: 'var(--space-xs)' }}>&ldquo;{r.message}&rdquo;</p>}

            <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
              {t('myOffer.fullAddressOnAccept')}
            </p>

            <div className="row" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-sm)' }}>
              <button
                type="button"
                className="btn btn-primary btn-sm btn-inline"
                disabled={busy}
                onClick={() => onRespond(r._id, true)}
              >
                <Check size={16} /> {t('myOffer.accept')}
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm btn-inline"
                disabled={busy}
                onClick={() => onRespond(r._id, false)}
              >
                {t('myOffer.noThanks')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
